import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  ResponsiveTable,
  ResponsiveTableBody,
  ResponsiveTableCell,
  ResponsiveTableHead,
  ResponsiveTableHeader,
  ResponsiveTableRow,
} from "@/components/ui/responsive-table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useToast } from "@/hooks/use-toast";
import { axiosInstance } from "@/pages/auth/auth.context";
import {
  EventPositionDTO,
  UserPublicInfoDto,
} from "@/pages/admin/events/types";
import { Mail, Phone, ArrowUpDown } from "lucide-react";
import { AssignEmployeeDialog } from "./AssignEmployeeDialog";

interface Assignment {
  user: UserPublicInfoDto;
  position: string;
  positionId: string;
}

interface EventStaffAssignmentCardProps {
  eventId: string;
  eventPositions: EventPositionDTO[];
  onStaffUpdate: () => void;
}

export default function EventStaffAssignmentCard({
  eventId,
  eventPositions,
  onStaffUpdate,
}: EventStaffAssignmentCardProps) {
  const { toast } = useToast();
  const [sortField, setSortField] = useState<
    "name" | "position" | "email" | "phone"
  >("name");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");
  const [removeModal, setRemoveModal] = useState<{
    open: boolean;
    user: UserPublicInfoDto | null;
    positionId: string | null;
  }>({
    open: false,
    user: null,
    positionId: null,
  });

  // Create assignments array from event positions
  const assignments: Assignment[] = eventPositions.flatMap((position) =>
    position.signedUpUsers.map((user) => ({
      user,
      position: position.tableName,
      positionId: position.eventPositionId,
    })),
  );

  const handleSort = (field: "name" | "position" | "email" | "phone") => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  const sortedAssignments = [...assignments].sort((a, b) => {
    let aValue: string;
    let bValue: string;

    switch (sortField) {
      case "name":
        aValue = `${a.user.firstName} ${a.user.lastName}`;
        bValue = `${b.user.firstName} ${b.user.lastName}`;
        break;
      case "position":
        aValue = a.position;
        bValue = b.position;
        break;
      case "email":
        aValue = a.user.email || "";
        bValue = b.user.email || "";
        break;
      case "phone":
        aValue = a.user.phoneNumber || "";
        bValue = b.user.phoneNumber || "";
        break;
      default:
        aValue = "";
        bValue = "";
    }

    if (sortDirection === "asc") {
      return aValue.localeCompare(bValue);
    } else {
      return bValue.localeCompare(aValue);
    }
  });

  const getSortIcon = (field: "name" | "position" | "email" | "phone") => {
    if (sortField !== field) {
      return <ArrowUpDown className="h-4 w-4 ml-1 text-muted-foreground" />;
    }
    return (
      <ArrowUpDown
        className={`h-4 w-4 ml-1 ${
          sortDirection === "asc"
            ? "text-foreground rotate-180"
            : "text-foreground"
        }`}
      />
    );
  };

  const handleRemoveEmployee = async () => {
    if (!removeModal.user || !removeModal.positionId) return;

    try {
      await axiosInstance.post(
        `${process.env.REACT_APP_BASE_API_URL}event/position/${removeModal.positionId}/remove/${removeModal.user.id}`,
      );

      toast({
        title: "Success",
        description: `${removeModal.user.firstName} ${removeModal.user.lastName} was removed from the event`,
        variant: "success",
      });

      onStaffUpdate();
      setRemoveModal({ open: false, user: null, positionId: null });
    } catch (error) {
      console.error("Error removing employee:", error);
      toast({
        title: "Error",
        description: "Failed to remove employee from event",
        variant: "destructive",
      });
    }
  };

  // Handler for when an employee is assigned
  const handleEmployeeAssigned = () => {
    onStaffUpdate();
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-y-2 sm:gap-y-0">
          <div className="flex flex-col sm:flex-row sm:items-center sm:gap-x-4 gap-y-2 sm:gap-y-0">
            <span className="text-left sm:text-inherit text-2xl font-semibold">
              Staff Assignments
            </span>
            <Badge variant="secondary" className="px-3 py-1 text-base">
              {assignments.length} assigned
            </Badge>
          </div>
          <div className="mt-2 sm:mt-0 sm:ml-auto">
            <AssignEmployeeDialog
              eventId={eventId}
              eventPositions={eventPositions}
              onEmployeeAssigned={handleEmployeeAssigned}
            />
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {assignments.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            No staff assigned to this event yet.
          </div>
        ) : (
          <div className="rounded-md border">
            <ResponsiveTable>
              <ResponsiveTableHeader>
                <ResponsiveTableRow>
                  <ResponsiveTableHead
                    onClick={() => handleSort("name")}
                    className="cursor-pointer hover:text-primary transition-colors"
                  >
                    Name {getSortIcon("name")}
                  </ResponsiveTableHead>
                  <ResponsiveTableHead
                    onClick={() => handleSort("position")}
                    className="cursor-pointer hover:text-primary transition-colors"
                  >
                    Position {getSortIcon("position")}
                  </ResponsiveTableHead>
                  <ResponsiveTableHead
                    onClick={() => handleSort("email")}
                    className="cursor-pointer hover:text-primary transition-colors"
                  >
                    Email {getSortIcon("email")}
                  </ResponsiveTableHead>
                  <ResponsiveTableHead
                    onClick={() => handleSort("phone")}
                    className="cursor-pointer hover:text-primary transition-colors"
                  >
                    Phone {getSortIcon("phone")}
                  </ResponsiveTableHead>
                  <ResponsiveTableHead>Action</ResponsiveTableHead>
                </ResponsiveTableRow>
              </ResponsiveTableHeader>
              <ResponsiveTableBody>
                {sortedAssignments.map((assignment, index) => (
                  <ResponsiveTableRow
                    key={`${assignment.user.id}-${index}`}
                    mobileTitle={`${assignment.user.firstName} ${assignment.user.lastName}`}
                  >
                    <ResponsiveTableCell
                      mobileLabel="Name"
                      mobilePriority="high"
                    >
                      <div className="font-medium">{`${assignment.user.firstName} ${assignment.user.lastName}`}</div>
                    </ResponsiveTableCell>
                    <ResponsiveTableCell
                      mobileLabel="Position"
                      mobilePriority="medium"
                    >
                      <Badge variant="outline">{assignment.position}</Badge>
                    </ResponsiveTableCell>
                    <ResponsiveTableCell
                      mobileLabel="Email"
                      mobilePriority="medium"
                    >
                      <div className="flex items-center text-sm text-muted-foreground">
                        <Mail className="mr-2 h-3 w-3" />
                        <span>{assignment.user.email}</span>
                      </div>
                    </ResponsiveTableCell>
                    <ResponsiveTableCell
                      mobileLabel="Phone"
                      mobilePriority="medium"
                    >
                      <div className="flex items-center text-sm text-muted-foreground">
                        <Phone className="mr-2 h-3 w-3" />
                        <span>
                          {assignment.user.phoneNumber || "Not provided"}
                        </span>
                      </div>
                    </ResponsiveTableCell>
                    <ResponsiveTableCell>
                      <Button
                        variant="destructive"
                        size="sm"
                        className="w-full sm:w-auto"
                        onClick={() =>
                          setRemoveModal({
                            open: true,
                            user: assignment.user,
                            positionId: assignment.positionId,
                          })
                        }
                      >
                        Remove
                      </Button>
                    </ResponsiveTableCell>
                  </ResponsiveTableRow>
                ))}
              </ResponsiveTableBody>
            </ResponsiveTable>
          </div>
        )}
      </CardContent>
      {/* Remove Employee Modal */}
      <AlertDialog
        open={removeModal.open}
        onOpenChange={(open) => setRemoveModal({ ...removeModal, open })}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove Employee</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to remove{" "}
              {removeModal.user
                ? `${removeModal.user.firstName} ${removeModal.user.lastName}`
                : "this employee"}{" "}
              from the event? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleRemoveEmployee}>
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}
